import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getEmpresas, getMonedas, getTCHoy } from '../services/api';
import api from '../services/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [empresas, setEmpresas] = useState([]);
  const [empresaActual, setEmpresaActualState] = useState(() => {
    const saved = localStorage.getItem('empresaActual');
    return saved ? JSON.parse(saved) : null;
  });
  const [monedas, setMonedas] = useState([]);
  const [tcHoy, setTcHoy] = useState({ tc_compra: 3.70, tc_venta: 3.75 });
  const [loading, setLoading] = useState(true);
  
  // AUTH STATE
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));

  // Función para cambiar empresa y guardar en localStorage
  const setEmpresaActual = (empresa) => {
    setEmpresaActualState(empresa);
    if (empresa) {
      localStorage.setItem('empresaActual', JSON.stringify(empresa));
    } else {
      localStorage.removeItem('empresaActual');
    }
  };

  // Login function
  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    setIsAuthenticated(true);
    
    // Set token in api headers
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  // Logout function
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const res = await api.post('/auth/refresh');
      const newToken = res.data.token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  // Set initial token header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Handle 401 errors - logout and redirect
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && isAuthenticated) {
          logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
    
    return () => api.interceptors.response.eject(interceptor);
  }, [isAuthenticated]);

  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [empresasRes, monedasRes, tcRes] = await Promise.all([
        getEmpresas(),
        getMonedas(),
        getTCHoy()
      ]);
      
      setEmpresas(empresasRes.data);
      setMonedas(monedasRes.data);
      setTcHoy(tcRes.data);
      
      // Si hay empresa guardada, verificar que aún existe
      const savedEmpresa = localStorage.getItem('empresaActual');
      if (savedEmpresa) {
        const parsed = JSON.parse(savedEmpresa);
        const exists = empresasRes.data.find(e => e.id === parsed.id);
        if (exists) {
          setEmpresaActualState(exists);
        } else {
          if (empresasRes.data.length > 0) {
            setEmpresaActual(empresasRes.data[0]);
          }
        }
      } else if (empresasRes.data.length > 0) {
        setEmpresaActual(empresasRes.data[0]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const refreshEmpresas = async () => {
    const res = await getEmpresas();
    setEmpresas(res.data);
    return res.data;
  };

  const getMonedaPEN = () => monedas.find(m => m.codigo === 'PEN');
  const getMonedaUSD = () => monedas.find(m => m.codigo === 'USD');

  const value = {
    empresas,
    empresaActual,
    setEmpresaActual,
    monedas,
    tcHoy,
    loading,
    refreshEmpresas,
    getMonedaPEN,
    getMonedaUSD,
    loadInitialData,
    // Auth
    token,
    user,
    isAuthenticated,
    login,
    logout,
    refreshToken
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
